import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Zap, Loader2, AlertTriangle, CheckCircle } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { restartQuizGeneration } from '@/services/restartQuizGeneration';
import { restartPracticeGeneration } from '@/services/restartPracticeGeneration';
import { regenerateAllWrongTopicQuizzes, WRONG_TOPIC_QUIZ_MODULES } from '@/services/quizRegenerationService';

export const ContentGenerationTrigger = () => {
  const [generating, setGenerating] = useState({ quizzes: false, labs: false, all: false, wrongTopic: false });
  const [regenerationResult, setRegenerationResult] = useState<{ succeeded: number; failed: number } | null>(null);

  // Auto-trigger wrong-topic quiz regeneration on mount
  useEffect(() => {
    handleRegenerateWrongTopicQuizzes();
  }, []);

  const handleRegenerateWrongTopicQuizzes = async () => {
    setGenerating(prev => ({ ...prev, wrongTopic: true }));
    setRegenerationResult(null);
    try {
      const result = await regenerateAllWrongTopicQuizzes();
      setRegenerationResult({ succeeded: result.succeeded, failed: result.failed });
      
      if (result.succeeded > 0) {
        toast({
          title: 'Wrong-Topic Quizzes Fixed',
          description: `Successfully regenerated ${result.succeeded}/${result.total} quizzes with relevant content.`,
        });
      }
      if (result.failed > 0) {
        toast({
          title: 'Some Quizzes Failed',
          description: `${result.failed} quizzes could not be regenerated. Check logs for details.`,
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to regenerate wrong-topic quizzes',
        variant: 'destructive'
      });
    } finally {
      setGenerating(prev => ({ ...prev, wrongTopic: false }));
    }
  };

  const handleGenerateQuizzes = async () => {
    setGenerating(prev => ({ ...prev, quizzes: true }));
    try {
      const result = await restartQuizGeneration();
      toast({
        title: 'Quiz Generation Started',
        description: result.message || 'Processing missing quizzes...'
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to start quiz generation',
        variant: 'destructive'
      });
    } finally {
      setGenerating(prev => ({ ...prev, quizzes: false }));
    }
  };

  const handleGenerateLabs = async () => {
    setGenerating(prev => ({ ...prev, labs: true }));
    try {
      const result = await restartPracticeGeneration();
      toast({
        title: 'Practice Lab Generation Started',
        description: result.message || 'Processing missing practice labs...'
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to start practice lab generation',
        variant: 'destructive'
      });
    } finally {
      setGenerating(prev => ({ ...prev, labs: false }));
    }
  };

  const handleGenerateAll = async () => {
    setGenerating(prev => ({ ...prev, all: true }));
    try {
      await Promise.all([
        restartQuizGeneration(),
        restartPracticeGeneration()
      ]);
      toast({
        title: 'Bulk Generation Started',
        description: 'Generating all missing quizzes and practice labs...'
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to start bulk generation',
        variant: 'destructive'
      });
    } finally {
      setGenerating(prev => ({ ...prev, all: false }));
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-primary" />
          Quick Generation Actions
        </CardTitle>
        <CardDescription>
          Generate missing quizzes and practice labs to achieve 100% coverage
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Wrong-Topic Quiz Regeneration Section */}
        <div className="mb-4 p-4 bg-destructive/10 rounded-lg border border-destructive/30">
          <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-destructive" />
            Fix {WRONG_TOPIC_QUIZ_MODULES.length} Wrong-Topic Quizzes
          </h3>
          <p className="text-xs text-muted-foreground mb-3">
            Regenerate quizzes with irrelevant content (geography trivia, wrong language questions, etc.)
          </p>
          {regenerationResult && (
            <div className="mb-3 p-2 bg-background rounded text-xs flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span>
                {regenerationResult.succeeded} fixed, {regenerationResult.failed} failed
              </span>
            </div>
          )}
          <Button
            onClick={handleRegenerateWrongTopicQuizzes}
            disabled={generating.wrongTopic}
            variant="destructive"
            size="sm"
            className="w-full"
          >
            {generating.wrongTopic ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Regenerating {WRONG_TOPIC_QUIZ_MODULES.length} Quizzes...
              </>
            ) : (
              <>
                <AlertTriangle className="w-4 h-4 mr-2" />
                Fix Wrong-Topic Quizzes Now
              </>
            )}
          </Button>
        </div>

        <div className="mb-4 p-4 bg-muted/50 rounded-lg border">
          <h3 className="text-sm font-semibold mb-2">🎯 Quiz Generation Status</h3>
          <p className="text-xs text-muted-foreground mb-3">
            Use the buttons below to generate missing quizzes or fix wrong-topic quizzes.
          </p>
        </div>
        
        <Button
          onClick={handleGenerateQuizzes}
          disabled={generating.quizzes || generating.all}
          className="w-full"
          variant="outline"
        >
          {generating.quizzes ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Generating Quizzes...
            </>
          ) : (
            'Generate Missing Quizzes'
          )}
        </Button>

        <Button
          onClick={handleGenerateLabs}
          disabled={generating.labs || generating.all}
          className="w-full"
          variant="outline"
        >
          {generating.labs ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Generating Labs...
            </>
          ) : (
            'Generate Missing Practice Labs'
          )}
        </Button>

        <Button
          onClick={handleGenerateAll}
          disabled={generating.quizzes || generating.labs || generating.all}
          className="w-full"
        >
          {generating.all ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Generating All...
            </>
          ) : (
            <>
              <Zap className="w-4 h-4 mr-2" />
              Generate All Missing Content
            </>
          )}
        </Button>

        <p className="text-xs text-muted-foreground text-center pt-2">
          This will automatically detect and generate only missing quizzes and labs
        </p>
      </CardContent>
    </Card>
  );
};
